import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from 'environments/environment';
import { Observable } from 'rxjs';
import * as braintreeClient from 'braintree-web/client';
import * as dataCollector from 'braintree-web/data-collector';
import { tap, catchError } from 'rxjs/operators';
import { PromotionsService } from 'app/services/promotions.service';
import { Bookformat } from 'app/interfaces/bookformat';
import { ActivatedRoute } from '@angular/router';
import { FirebaseService } from './firebase.service';

@Injectable()
export class PrintService {
  private baseUrl = environment.API;
  specs;
  bookformats: Bookformat[];
  storyId;
  projectId;
  printOrder: any = {};
  shippingAddress: any;
  selectedCard: any;
  selectedBookFormat: any;
  deviceData: any;
  braintreeClientInstance: any;

  constructor(
    private http: HttpClient,
    private promotionsService: PromotionsService,
    public activatedRoute: ActivatedRoute,
    private firebaseService: FirebaseService,
  ) {
    this.initializeWizard();
  }

  initBraintree() {
    this.getToken().subscribe(data => {
      braintreeClient.create(
        {
          authorization: data['token']
        },
        (createError, clientInstance) => {
          if (createError) {
            this.firebaseService.logEvent('checkout_payment_config_error');
            throw createError;
          }
          this.braintreeClientInstance = clientInstance;
          this.getDeviceData();
        }
      );
    });
  }

  fetchStory(storyId) {
    const url = `${this.baseUrl}/stories/${storyId}/`;
    return this.http.get(url);
  }

  getDeviceData() {
    dataCollector.create(
      {
        client: this.braintreeClientInstance,
        kount: true,
      },
      (error, dataCollectorInstance) => {
        if (error) {
          throw error;
        }

        this.deviceData = dataCollectorInstance.deviceData;
      }
    );
  }

  loadBookformats(id, discountCode = '') {
    this.storyId = id;
    const url = `${this.baseUrl}/bookformats/story/${id}/`;
    const code = discountCode || this.promotionsService.getActive();
    const params = code
      ? new HttpParams().set('discount_code', code)
      : new HttpParams();

    return this.http.get(url, { params }).pipe(
      tap(data => {
        this.bookformats = data['results'];
        if (this.bookformats.length) {
          this.specs.book_format_id =
            this.specs.book_format_id ||
            this.getDefaultItem(this.bookformats).id;
          this.specs.discount_code =
            this.getDefaultItem(this.bookformats).discount_code || '';
        }
      })
    );
  }

  private getDefaultItem(collection) {
    return collection.find(item => item.is_default) || collection[0];
  }

  getBookformatsWithFallback(id, discountCode = '') {
    return this.loadBookformats(id, discountCode).pipe(
      catchError(() => {
        this.promotionsService.clear();
        return this.loadBookformats(id, '');
      })
    );
  }

  getAddresses() {
    const url = `${this.baseUrl}/users/addresses/`;

    return this.http.get(url);
  }

  saveAddress(address) {
    if (address.uuid) {
      const url = `${this.baseUrl}/users/addresses/${address.uuid}/`;

      return this.http.put(url, address);
    } else {
      const url = `${this.baseUrl}/users/addresses/`;

      return this.http.post(url, address);
    }
  }

  deleteAddresses(address) {
    const url = `${this.baseUrl}/users/addresses/${address.uuid}/`;

    return this.http.delete(url);
  }

  getDeliveryOptions(storyId) {
    const url = `${this.baseUrl}/deliveryoptions/bookformat/${this.specs
      .book_format_id}/story/${storyId}`;

    return this.http.get(url);
  }

  getToken() {
    const url = `${this.baseUrl}/payments/token/`;

    return this.http.get(url);
  }

  getPaymentMethods() {
    const url = `${this.baseUrl}/payments/methods/`;

    return this.http.get(url);
  }

  createPaymentMethod(params) {
    const url = `${this.baseUrl}/payments/methods/`;

    return this.http.post(url, params);
  }

  applyDiscount(code) {
    this.specs.discount_code = code;
    return this.save();
  }

  createPrintOrder() {
    const url = `${this.baseUrl}/printorders/story/${this.storyId}/`;
    return this.http.post(
      url,
      this.specs.discount_code
        ? {
            book_format_id: this.specs.book_format_id,
            delivery_option_id: this.specs.delivery_option_id,
            shipping_address_id: this.specs.shipping_address_id,
          }
        : this.specs
    );
  }

  updatePrintOrder() {
    const url = `${this.baseUrl}/printorders/${this.printOrder.id}/`;
    return this.http.put(url, this.specs);
  }

  changeQuantity(quantity) {
    this.specs.quantity = quantity;
    return this.save();
  }

  save() {
    const orderSave$ = this.printOrder.id
      ? this.updatePrintOrder()
      : this.createPrintOrder();

    return Observable.create(observer => {
      orderSave$.subscribe(
        data => {
          this.printOrder = data;
          observer.next(data);
          observer.complete();
        },
        data => {
          observer.error(data);
        }
      );
    });
  }

  purchase() {
    const url = `${this.baseUrl}/printorders/${this.printOrder.id}/purchase/`;
    const params = {
      payment_method_token: this.selectedCard && this.selectedCard.token,
      device_data: this.deviceData,
    };

    return this.http.post(url, params).pipe(
      tap(() => {
        this.promotionsService.clear();
      })
    );
  }

  initializeWizard() {
    this.specs = {
      book_format_id: '',
    };
    this.printOrder = {};
    this.bookformats = undefined;
    this.selectedCard = undefined;
    this.shippingAddress = undefined;
  }
}
